import {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {InfinityScroll} from '../../design-system/atoms/InfinityScroll/InfinityScroll';
import {Loading} from '../../design-system/atoms/Loading/Loading';
import {
  StaticLandingEventSection,
  StaticLandingEventSectionQueryVariables,
} from '../../graphql/generated/types';
import {onSuccessStaticLandingSection} from '../../pagewebLayout/PagewebLayoutSlice';
import {CACHE_ONLY_CONFIG} from '../../reactQuery/constant';
import {useInfiniteStaticLandingEventSectionQuery} from '../../wrapper/landingWrapper';
import useTypedSelector from '../useTypedSelector';
import {StaticLandingEventMoreFetcherProps} from './StaticLandingEventMoreFetcherProps.interface';

export function StaticLandingEventMoreFetcher({
  children,
  section,
}: StaticLandingEventMoreFetcherProps) {
  const dispatch = useDispatch();
  const staticLandingEventSection = useTypedSelector(
    state =>
      state.pagewebLayout.entities.sections[section.id] as Omit<
        StaticLandingEventSection,
        'groups'
      >
  );
  const {param: currentParam} = staticLandingEventSection;

  const param: StaticLandingEventSectionQueryVariables['param'] = {
    categoryUid: currentParam.categoryUid,
  };

  const {data, isSuccess, fetchNextPage, isFetchedAfterMount, isFetching} =
    useInfiniteStaticLandingEventSectionQuery(
      {
        sectionId: section.id,
        param,
      },
      {
        initialPageParam: {sectionId: section.id, param},
        getNextPageParam: lastPage => {
          const staticLandingEventSection =
            lastPage.staticLandingEventSection as StaticLandingEventSection;
          return staticLandingEventSection?.isEnd
            ? undefined
            : {
                param: {
                  ...param,
                  page: (staticLandingEventSection.param.page || 0) + 1,
                },
                sectionId: section.id,
              };
        },
        initialData: staticLandingEventSection
          ? {
              pages: [
                {
                  staticLandingEventSection:
                    staticLandingEventSection as StaticLandingEventSection,
                },
              ],
              pageParams: [{sectionId: section.id, param}],
            }
          : undefined,
        ...CACHE_ONLY_CONFIG,
      }
    );

  const lastPage = data?.pages[data.pages.length - 1]
    ?.staticLandingEventSection as StaticLandingEventSection | undefined;
  useEffect(() => {
    if (isFetchedAfterMount && isSuccess) {
      dispatch(
        onSuccessStaticLandingSection(lastPage, {
          sectionId: section.id,
          page: (lastPage as StaticLandingEventSection)?.param.page || 0,
        })
      );
    }
  }, [dispatch, isFetchedAfterMount, isSuccess, lastPage, section.id]);

  return (
    <>
      <InfinityScroll
        isLoading={isFetching}
        canLoadNextPage={!isFetching && !lastPage?.isEnd}
        requestNextPageData={fetchNextPage}
      >
        {children}
      </InfinityScroll>
      {isFetching && (
        <div className="flex-center-full">
          <Loading />
        </div>
      )}
    </>
  );
}
